// Shared planner pieces: identity avatar, the bare count numeral, person/gap text lines,
// the per-line time + flags menu, staff-flag glyphs and the searchable assign picker. Used
// by both the Day Table and Timeline layouts. The density redesign replaced the old bordered
// chips and icon+word badges with these — colour is reserved for counts, exceptions and the
// (configurable, deliberately small) staff-flag glyphs.
import { useRef, useState } from "react";
import { AlertTriangle, Check, Clock3, RotateCcw, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { resolveFlagIcon } from "@/lib/work-plan-flag-icons";
import { flagTone, toggleFlagId } from "@/lib/work-plan-flags";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { identityTone, initialsFromName, workRoleLabel } from "./work-plan-utils";

/**
 * The person's photo, falling back to tinted initials keyed on a stable id (person or
 * clinic). photoUrl is the self-authenticating avatar URL from the day bundle — Radix's
 * <AvatarImage> is a plain <img>, so it carries its own ticket and needs no header; a
 * missing or broken photo simply renders the initials.
 */
export function IdentityAvatar({ id, name, photoUrl = "", size = 24, className }) {
  const tone = identityTone(id);
  const initials = (
    <AvatarFallback
      className="font-bold"
      style={{ background: tone.bg, color: tone.fg, fontSize: Math.max(9, Math.round(size * 0.4)) }}
    >
      {initialsFromName(name)}
    </AvatarFallback>
  );
  return (
    <Avatar className={cn("shrink-0", className)} style={{ width: size, height: size }} aria-hidden>
      {photoUrl ? <AvatarImage src={photoUrl} alt="" className="object-cover" /> : null}
      {initials}
    </Avatar>
  );
}

/**
 * A time input whose clock sits at the FIELD's right edge in the app's own muted tone.
 * Chromium's native picker button can be recoloured but never repositioned (it renders
 * immediately after the digits), so it is made transparent and a real icon is drawn in
 * its place; clicking it opens the same native picker.
 */
export function TimeField({ value, onChange, className, ...props }) {
  const inputRef = useRef(null);
  return (
    <span className="relative block w-full min-w-0">
      <Input
        ref={inputRef}
        type="time"
        value={value}
        onChange={onChange}
        // display:none, not opacity-0 — an invisible picker button still takes layout
        // width, which pushed the field past the popover's edge in the two-up row.
        className={cn(
          "h-8 min-w-0 pr-7 text-xs [&::-webkit-calendar-picker-indicator]:hidden",
          className,
        )}
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Open the time picker"
        className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
        onClick={() => inputRef.current?.showPicker?.()}
      >
        <Clock3 className="size-3.5" />
      </button>
    </span>
  );
}

/**
 * The booked-load count as a bare numeral — the redesign's replacement for the old
 * icon+word badge everywhere. Number first (14px, sky), tiny tracking-caps label second;
 * baseline-aligned so it reads as one token. Dims when the session is empty so a wall of
 * zeroes doesn't compete with real load.
 */
export function CountNumeral({ count = 0, label = "appts", title, className }) {
  const value = Number(count) || 0;
  return (
    <span
      className={cn("inline-flex shrink-0 items-baseline gap-1 leading-none", className)}
      title={title ?? `${value} appointment${value === 1 ? "" : "s"} booked in this session`}
    >
      <span
        className={cn(
          "text-sm font-black tabular-nums",
          value > 0 ? "text-sky-700 dark:text-sky-300" : "text-muted-foreground/50",
        )}
      >
        {value}
      </span>
      <span className="text-[8px] font-black uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </span>
    </span>
  );
}

/**
 * The mid-day move marker: arrow + the OTHER half of the person's day as a small AM/PM
 * label and a 3-letter clinic code ("→ ᴾᴹ CLE"). The session label rides small so the
 * destination stays the loudest part; the full story lives in the tooltip. Amber flags a
 * move a planner can act on; doctor markers pass `muted` and stay gray, matching every
 * other read-only cue on their lines.
 */
export function MoveMarker({ flag, muted = false, className }) {
  if (!flag?.moves) return null;
  const parts = flag.shortParts || [{ session: "", code: flag.shortDetail }];
  return (
    <span
      className={cn(
        "flex shrink-0 items-baseline gap-1 font-bold",
        muted ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400",
        className,
      )}
      title={flag.title}
    >
      <span>{flag.back ? "←" : "→"}</span>
      {parts.map((part, index) => (
        <span key={index} className="flex items-baseline gap-[3px]">
          {part.session ? (
            <span className="text-[8px] font-black uppercase tracking-wide opacity-80">
              {part.session}
            </span>
          ) : null}
          {part.code}
        </span>
      ))}
    </span>
  );
}

/**
 * The staff flags on a posting as tiny tone-coloured glyphs (≤ `max`, then "+N"). Each
 * glyph's tooltip carries "label — description" so the meaning is one hover away; the
 * line/bar title folds the labels in too. Nothing renders for a posting without flags or a
 * doctor entry, so a flag-less day looks exactly as it did before flags existed.
 */
export function FlagGlyphs({ flags, max = 3, className }) {
  const list = (flags || []).filter((flag) => flag?.id);
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <span className={cn("flex shrink-0 items-center gap-0.5", className)} aria-label="Flags">
      {shown.map((flag) => {
        const Icon = resolveFlagIcon(flag.icon);
        return (
          <span
            key={flag.id}
            className="flex items-center"
            title={flag.description ? `${flag.label} — ${flag.description}` : flag.label}
          >
            <Icon className={cn("size-3", flagTone(flag.colorId).glyph)} aria-label={flag.label} />
          </span>
        );
      })}
      {rest > 0 ? (
        <span
          className="text-[9px] font-bold leading-none text-muted-foreground"
          title={list
            .slice(max)
            .map((flag) => flag.label)
            .join(", ")}
        >
          +{rest}
        </span>
      ) : null}
    </span>
  );
}

/**
 * One person as a text line — the Day Table's replacement for the bordered chip. Small
 * avatar + name, then EXCEPTIONS only: staff-flag glyphs, the time when it differs from the
 * session hours (doctor part-sessions, overrides), an amber "→ CLE" when the person moves
 * clinics mid-day, "AM only" for a half day. Support staff read bold and open the time/flags
 * menu; doctors read muted and carry only their tooltip — the muting IS the read-only signal.
 * `flagDefs` is the day's flag vocabulary (empty on a backend without flags → no menu section).
 */
export function PersonLine({ entry, session, canManage, actions, flagDefs = [] }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuGeneration, setMenuGeneration] = useState(0);
  const flag = entry.dayFlag;
  const customTime =
    entry.start && entry.end && (entry.start !== session.start || entry.end !== session.end)
      ? `${entry.start}–${entry.end}`
      : "";
  const editable = !entry.locked && canManage;
  const flagNames = (entry.flags || []).map((item) => item.label).join(", ");

  const body = (
    <div
      className={cn(
        "group/line -mx-1.5 flex h-[26px] items-center gap-[7px] rounded-[7px] px-1.5 text-left transition-colors duration-120",
        editable && "cursor-pointer hover:bg-muted",
      )}
      title={
        entry.locked
          ? `${entry.title} — rostered in Sessions — read-only here`
          : entry.mcPending
            ? `${entry.title} — MC pending`
            : `${entry.title} · ${entry.subtitle}${flagNames ? ` · ${flagNames}` : ""}`
      }
    >
      <IdentityAvatar id={entry.avatarId} name={entry.avatarName} photoUrl={entry.avatarPhotoUrl} size={18} />
      <span
        className={cn(
          "min-w-0 truncate text-[12.5px] leading-tight",
          entry.locked ? "font-normal text-muted-foreground" : "font-bold text-foreground",
          entry.mcPending && "text-rose-700 line-through dark:text-rose-300",
        )}
      >
        {entry.title}
      </span>
      <FlagGlyphs flags={entry.flags} />
      {customTime ? (
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{customTime}</span>
      ) : null}
      {flag?.moves ? (
        <MoveMarker flag={flag} muted={entry.locked} className="text-[11px]" />
      ) : flag ? (
        <span className="shrink-0 text-[11px] text-muted-foreground" title={flag.title}>
          {flag.session} only
        </span>
      ) : null}
      {entry.mcPending ? (
        <span className="shrink-0 rounded bg-rose-100 px-1 text-[9px] font-bold tracking-wide text-rose-600 dark:bg-rose-900/60 dark:text-rose-300">
          MC
        </span>
      ) : null}
      {editable ? (
        // NOT a <button>: the whole line is already the menu-trigger button, and HTML
        // forbids nesting buttons. Keyboard users reach Remove through the line's menu;
        // this hover affordance is the pointer shortcut.
        <span
          role="button"
          aria-label="Remove from this session"
          className="ml-auto flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity duration-120 hover:bg-rose-100 hover:text-rose-600 group-hover/line:opacity-100 dark:hover:bg-rose-950"
          title="Remove from this session"
          onClick={(event) => {
            event.stopPropagation();
            actions.remove(entry.posting);
          }}
        >
          <X className="size-3" />
        </span>
      ) : null}
    </div>
  );

  if (!editable) {
    return body;
  }
  return (
    <Popover
      open={menuOpen}
      onOpenChange={(next) => {
        setMenuOpen(next);
        // Remount the menu on every OPEN (not on close, which would flash the menu
        // through the exit animation): ChipMenuContent stays mounted while the popover is
        // shut, so without this it reopens still on the time form, with stale times.
        if (next) setMenuGeneration((generation) => generation + 1);
      }}
    >
      <PopoverTrigger asChild>
        <button type="button" className="block w-full text-left">
          {body}
        </button>
      </PopoverTrigger>
      <ChipMenuContent
        key={menuGeneration}
        title={entry.title}
        sessionRange={`${session.start} – ${session.end}`}
        customTime={customTime}
        flagDefs={flagDefs}
        flagIds={entry.flagIds || []}
        onToggleFlag={(flagId) => {
          // The popover stays open: toggling several flags in a row is the common case, and
          // the row re-renders with the new ids (optimistic applyLocal) so the ticks track.
          actions.setFlags(entry.posting, toggleFlagId(entry.flagIds, flagId));
        }}
        onSetTimes={(start, end) => {
          setMenuOpen(false);
          actions.setTimes(entry.posting, start, end);
        }}
        onResetTimes={() => {
          setMenuOpen(false);
          actions.resetTimes(entry.posting);
        }}
        onRemove={() => {
          setMenuOpen(false);
          actions.remove(entry.posting);
        }}
      />
    </Popover>
  );
}

/**
 * The flag vocabulary as toggle rows inside a posting menu: glyph · label · tick when on.
 * Renders nothing when the vocabulary is empty (or the backend predates flags), so the menu
 * keeps its old three-item shape there.
 */
export function FlagToggleRows({ flagDefs, flagIds, onToggle }) {
  const defs = (flagDefs || []).filter((def) => def?.id);
  if (defs.length === 0) return null;
  const active = new Set(flagIds || []);
  return (
    <>
      <div className="-mx-2 my-1 h-px bg-border" />
      <p className="px-2 pb-0.5 pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Flags</p>
      {defs.map((def) => {
        const Icon = resolveFlagIcon(def.icon);
        const on = active.has(def.id);
        return (
          <button
            key={def.id}
            type="button"
            aria-pressed={on}
            title={def.description || def.label}
            className={cn(
              "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted",
              on && "font-semibold",
            )}
            onClick={() => onToggle(def.id)}
          >
            <Icon className={cn("size-3.5", flagTone(def.colorId).glyph)} />
            <span className="min-w-0 flex-1 truncate">{def.label}</span>
            {on ? <Check className="size-3.5 shrink-0 text-primary" /> : null}
          </button>
        );
      })}
    </>
  );
}

function ChipMenuContent({
  title,
  sessionRange,
  customTime,
  flagDefs = [],
  flagIds = [],
  onToggleFlag,
  onSetTimes,
  onResetTimes,
  onRemove,
}) {
  const [editing, setEditing] = useState(false);
  const [defaultStart = "", defaultEnd = ""] = (customTime || sessionRange).split(/\s*[–-]\s*/);
  const [from, setFrom] = useState(defaultStart);
  const [to, setTo] = useState(defaultEnd);
  // w-64, not w-60: the two-up FROM/TO row sat within a few pixels of the popover edge,
  // which is no margin at all once a wider font renders the digits.
  return (
    <PopoverContent align="start" className="w-64 p-2" onOpenAutoFocus={(event) => event.preventDefault()}>
      {!editing ? (
        <div className="flex flex-col gap-0.5">
          <p className="px-2 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {title}
          </p>
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted"
            onClick={() => setEditing(true)}
          >
            <Clock3 className="size-3.5 text-muted-foreground" />
            Override session time
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted"
            onClick={onResetTimes}
          >
            <RotateCcw className="size-3.5 text-muted-foreground" />
            Reset to clinic hours
          </button>
          <FlagToggleRows flagDefs={flagDefs} flagIds={flagIds} onToggle={onToggleFlag} />
          <div className="-mx-2 my-1 h-px bg-border" />
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
            onClick={onRemove}
          >
            <X className="size-3.5" />
            Remove from this session
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 p-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Custom time · default {sessionRange}
          </p>
          <div className="flex gap-2">
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              From
              <TimeField value={from} onChange={(event) => setFrom(event.target.value)} />
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              To
              <TimeField value={to} onChange={(event) => setTo(event.target.value)} />
            </label>
          </div>
          {/* Cancel goes BACK to the menu rather than closing the popover — this form is
              one misclick away from the chip, and losing the whole menu to get out of it
              is the wrong penalty. */}
          <div className="flex gap-1.5">
            <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="flex-[2]"
              disabled={!from || !to || to <= from}
              onClick={() => onSetTimes?.(from, to)}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </PopoverContent>
  );
}

/**
 * The line a confirmed MC leaves behind — the redesign's rose-tinted person line, no
 * dashed box: "Needs cover — <name> on MC" plus a small outline Find-cover button.
 */
export function GapLine({ whoName, isDoctor = false, canManage = false, onFindCover }) {
  return (
    <div className="-mx-1.5 flex h-[26px] items-center gap-[7px] rounded-[7px] px-1.5">
      <AlertTriangle className="size-3.5 shrink-0 text-rose-500 dark:text-rose-400" />
      <span className="min-w-0 truncate text-[12.5px] leading-tight text-rose-600 dark:text-rose-400">
        <span className="font-bold">Needs cover</span> — {whoName} on MC
      </span>
      {canManage ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="ml-auto h-6 shrink-0 border-rose-200 px-2 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950/40"
          onClick={onFindCover}
        >
          {isDoctor ? "Fix in Sessions" : "Find cover"}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Searchable assign picker. In clinic rows it lists support staff (doctors excluded, MC
 * people disabled); in employee rows it lists clinics.
 */
export function CellPicker({ mode, options, onPick, children, open, onOpenChange, align = "start" }) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-64 p-0">
        <Command>
          <CommandInput placeholder={mode === "clinic" ? "Search staff…" : "Search clinic…"} />
          <CommandList>
            <CommandEmpty>Nobody left to add.</CommandEmpty>
            {mode === "clinic" ? (
              <p className="px-3 pb-1 pt-2 text-[10.5px] leading-snug text-muted-foreground">
                Doctors aren't listed — their sessions come from the Sessions roster.
              </p>
            ) : null}
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={`${option.name} ${option.hint || ""}`}
                  disabled={option.blocked}
                  onSelect={() => {
                    if (!option.blocked) onPick(option.id);
                  }}
                  className="gap-2"
                >
                  <IdentityAvatar id={option.id} name={option.name} photoUrl={option.photoUrl} size={22} />
                  <span className="min-w-0 flex-1 truncate text-xs">{option.name}</span>
                  <span className="text-[10.5px] text-muted-foreground">
                    {option.blocked ? "On MC" : option.hint}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Builds picker options for a cell in clinic mode (support staff not already in it). */
export function staffPickerOptions(staff, excludeIds, roleView) {
  return staff
    .filter((person) => !person.isDoctor && !excludeIds.includes(person.id))
    .map((person) => ({
      id: person.id,
      name: person.displayName,
      photoUrl: person.avatarUrl || "",
      hint: workRoleLabel(person, roleView),
      blocked: Boolean(person.mcStatus),
    }));
}

/** Builds picker options for a cell in employee mode (clinics not already in it). */
export function clinicPickerOptions(clinics, excludeIds) {
  return clinics
    .filter((clinic) => !excludeIds.includes(clinic.id))
    .map((clinic) => ({
      id: clinic.id,
      name: clinic.name,
      hint: clinic.shortName || "",
      blocked: false,
    }));
}
